import { isNestedFilterType } from "../../config/filter-value-type-behavior";
import {
  FilterValueType,
  SuggestionSourceType,
  getFilterDefinitionByKey,
} from "../../config/structured-search-schema";
import { getVersionedEntityConfig } from "../../config/versioned-entity-config";
import { StructuredSearchField, StructuredSearchSuggestionField } from "../../config/types";
import {
  filterUsesAggregationSuggestions,
  getAggregationSuggestionField,
} from "../../config/suggestion-config";

import type { StructuredSearchStore } from "./structured-search-store";

const FIELDS_WITHOUT_VALUE_SUGGESTIONS: string[] = [
  StructuredSearchField.RequestId,
  StructuredSearchField.InputText,
  StructuredSearchField.OutputText,
  StructuredSearchField.Cost,
  StructuredSearchField.Latency,
  "turn_count",
  "tool_call_count",
];

const OPERATORS_WITHOUT_VALUE_SUGGESTIONS = new Set(["key_contains", "key_not_contains"]);

export const shouldSuppressSuggestionsDropdown = (
  field: string | undefined,
  operator?: string,
): boolean =>
  (!!field && FIELDS_WITHOUT_VALUE_SUGGESTIONS.includes(field)) ||
  (!!operator && OPERATORS_WITHOUT_VALUE_SUGGESTIONS.has(operator));

export const fieldHasSuggestions = (store: StructuredSearchStore): boolean => {
  const definition = getFilterDefinitionByKey(store.schema, store.inProgress.field);
  return definition ? filterUsesAggregationSuggestions(definition) : false;
};

export const shouldShowSuggestionsForValueStep = (
  field: string | undefined,
  nestedKeyMode: boolean,
  nestedValueMode: boolean,
  promptVersionStep: boolean,
): boolean => {
  if (!field) {
    return false;
  }
  if (shouldSuppressSuggestionsDropdown(field)) {
    return false;
  }
  return nestedKeyMode || nestedValueMode || promptVersionStep;
};

enum NestedSuggestionMode {
  None = "none",
  Keys = "keys",
  Values = "values",
}

const getNestedSuggestionMode = (store: StructuredSearchStore): NestedSuggestionMode => {
  const type = getFilterDefinitionByKey(store.schema, store.inProgress.field)?.type;
  if (!type || !isNestedFilterType(type)) {
    return NestedSuggestionMode.None;
  }

  if (store.step === "key") {
    return NestedSuggestionMode.Keys;
  }

  if (store.step === "value" && store.inProgress.nestedKey) {
    return NestedSuggestionMode.Values;
  }

  return NestedSuggestionMode.None;
};

export const shouldSuggestNestedKeys = (store: StructuredSearchStore): boolean =>
  getNestedSuggestionMode(store) === NestedSuggestionMode.Keys;

export const shouldSuggestNestedValues = (store: StructuredSearchStore): boolean =>
  getNestedSuggestionMode(store) === NestedSuggestionMode.Values;

export const getNestedSuggestionKey = (store: StructuredSearchStore): string | undefined =>
  shouldSuggestNestedValues(store) ? store.inProgress.nestedKey : undefined;

export const getSuggestionField = (store: StructuredSearchStore): string => {
  const { field } = store.inProgress;
  const mode = getNestedSuggestionMode(store);
  const definition = getFilterDefinitionByKey(store.schema, field);
  const nestedConfig =
    definition?.suggestions?.source === SuggestionSourceType.Nested
      ? definition.suggestions
      : undefined;

  if (nestedConfig) {
    if (mode === NestedSuggestionMode.Keys) {
      return nestedConfig.keysField;
    }
    if (mode === NestedSuggestionMode.Values) {
      return nestedConfig.valuesField;
    }
  }

  if (definition?.type === FilterValueType.Prompt) {
    return StructuredSearchSuggestionField.Prompt;
  }

  if (store.isVersionedEntityVersionStep) {
    const versionedEntityConfig = getVersionedEntityConfig(store.schema, field);
    if (versionedEntityConfig) {
      return versionedEntityConfig.versionsSuggestionField;
    }
  }

  if (definition && filterUsesAggregationSuggestions(definition)) {
    return getAggregationSuggestionField(definition);
  }

  return field ?? "";
};
